import { useCallback } from 'react';
import { LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { ActivityItem } from 'app/data/types/activity';
import { MODULE_COMMAND_VALUE } from 'app/shared/consts';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { usePortalMfeButton } from 'app/shared/hooks/use-portal-mfe-button';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';

/** Response of the RB single-resource endpoint (we only need the resource's real uuid). */
interface ResourceResponseBody {
  data: { resource?: { uuid?: string } };
}

/**
 * The resource's real UUID, carried directly on the feed item's `book_again` routing details.
 *
 * search-service projects this from the resource-booked event's `resource.uuid`, which is validated as
 * a UUID on ingest (and deliberately excluded on the update path, where helix sometimes sends a numeric
 * id there) — so a feed item never needs the numeric→uuid resolve below.
 */
const resourceUuidOf = (item: ActivityItem): string | undefined =>
  item.actions?.find((action) => action.type === 'book_again')?.routingDetails?.resourceUuid;

interface UseBookAgain {
  canBookAgain: (item: ActivityItem) => boolean;
  openBookAgain: (item: ActivityItem) => Promise<void>;
  /** Whether a resource id can be opened (has an id + the RB base url + building). */
  canOpenResource: (resourceId: string | undefined) => boolean;
  /** Resolve a NUMERIC resource id → uuid and deeplink to its details page. */
  openResource: (resourceId: string | undefined) => Promise<void>;
}

/**
 * "Book again" opens the booking's RESOURCE details page (ready to re-book), deeplinking to
 * `/resource-booking/resource/{uuid}` with the context params the route needs (`locationId` +
 * `singleAppInstanceConfig` + `skipRedirect`, matching the utility-button format). The RB details route
 * needs the resource's real UUID — a numeric id redirects to the RB home (verified).
 *
 * Two callers, two shapes of input:
 *  - the FEED (`openBookAgain`) already has the real uuid on the action's routing details, so it
 *    deeplinks straight away — no resolve request at all.
 *  - the BOOKING DETAIL page (`openResource`) reads `details.resource_id` off the transactions
 *    endpoint, which is NUMERIC, so it still resolves the uuid via the single-resource endpoint first.
 */
export const useBookAgain = (): UseBookAgain => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const sdk = useMiniappSdk();
  const portalMfeButtonUuid = usePortalMfeButton();

  const canOpenResource = useCallback(
    (resourceId: string | undefined): boolean => !!resourceId && !!process.env.REACT_APP_RESOURCE_BOOKING_URL && !!buildingUuid && !!apiUrl,
    [buildingUuid, apiUrl],
  );

  /** Build the RB details url for a resolved resource uuid and hand it to the host. */
  const deeplinkToResource = useCallback(
    (resourceUuid: string): void => {
      if (!process.env.REACT_APP_RESOURCE_BOOKING_URL || !buildingUuid) return;
      const base = process.env.REACT_APP_RESOURCE_BOOKING_URL.replace(/\/+$/, '');
      const url = `${base}/resource-booking/resource/${resourceUuid}`
        + `?locationId=${encodeURIComponent(buildingUuid)}&singleAppInstanceConfig=true&skipRedirect=true`;

      // Naming the button lets the tenant portal route to its resource-booking MFE instead of framing
      // the miniapp — and the resource survives, because the portal reads it back out of this url.
      // Other hosts ignore the uuid and open the url exactly as before.
      const utilityButtonUuid = portalMfeButtonUuid('resource_booking');

      if (sdk?.redirectToContent) {
        sdk.redirectToContent({
          // A module: "book again" opens the resource picker to make a NEW booking, so a host with
          // a record pane keeps it on its own route rather than framing it beside the list.
          value: MODULE_COMMAND_VALUE,
          type: LinkTypeEnum.WEBVIEW,
          options: { url, ...(utilityButtonUuid ? { uuid: utilityButtonUuid } : {}) },
        });
      } else {
        window.open(url, '_self');
      }
    },
    [buildingUuid, sdk, portalMfeButtonUuid],
  );

  const openResource = useCallback(
    async (resourceId: string | undefined): Promise<void> => {
      if (!resourceId || !process.env.REACT_APP_RESOURCE_BOOKING_URL || !buildingUuid || !apiUrl) return;

      let resourceUuid: string | undefined;
      try {
        const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
        const payload = await apiRequest<ResourceResponseBody, void, void>({
          url: `${apiUrl}/api/resource-booking/v2/building/${encodeURIComponent(buildingUuid)}/resources/${encodeURIComponent(resourceId)}`,
          method: HttpMethod.GET,
          headers,
        });
        resourceUuid = !payload.isEmpty() ? payload.data.data?.resource?.uuid : undefined;
      } catch {
        return; // resolve failed → no-op rather than a broken deeplink
      }
      if (!resourceUuid) return;

      deeplinkToResource(resourceUuid);
    },
    [apiUrl, buildingUuid, getAuthHeaders, getDefaultHeaders, deeplinkToResource],
  );

  const canBookAgain = useCallback((item: ActivityItem): boolean => canOpenResource(resourceUuidOf(item)), [canOpenResource]);
  // The feed's action already carries the real uuid, so this deeplinks directly — no resolve round-trip.
  const openBookAgain = useCallback(
    async (item: ActivityItem): Promise<void> => {
      const resourceUuid = resourceUuidOf(item);
      if (!resourceUuid || !apiUrl) return;
      deeplinkToResource(resourceUuid);
    },
    [apiUrl, deeplinkToResource],
  );

  return { canBookAgain, openBookAgain, canOpenResource, openResource };
};
